<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="content-card-slider-title" :field="fields?.title" tag="h2" />
      <JssRichText class="content-card-slider-desc" :field="fields?.subtitle" />
      <div class="content-card-slider-wrapper" v-if="fields?.list?.length > 0">
        <div
          v-for="(item, index) in fields?.list"
          :key="index"
          class="content-card-slider-wrapper-item"
          :class="{ 'is-active': state.activeIndex === index }"
          @mouseenter="handleCardEnter(index)"
          @click="handleCardClick(index)"
        >
          <SiteLink :link="utils.sc.getFieldObj(item, 'link')">
            <div class="content-card-slider-wrapper-item-bg">
              <AdaptiveImage
                class="content-card-slider-wrapper-item-bg-before"
                is-background
                :field="item.fields?.beforeImage"
              />
              <AdaptiveImage
                class="content-card-slider-wrapper-item-bg-after"
                is-background
                :field="item.fields?.afterImage"
              />
            </div>
            <div class="content-card-slider-wrapper-item-content">
              <JssRichText
                class="content-card-slider-wrapper-item-content-title"
                :field="item?.fields?.title"
                tag="h3"
              />
              <JssRichText
                class="content-card-slider-wrapper-item-content-desc"
                :field="item?.fields?.description"
              />
            </div>
            <div v-if="state.activeIndex === index" class="content-card-slider-wrapper-item-arrow">
              <Icon :svg="fields?.icon?.value?.svgCode" />
            </div>
          </SiteLink>
        </div>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { reactive, computed } from 'vue';
import utils from '@/utils';

export default {
  name: 'ContentCardSlider',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const state = reactive({
      activeIndex: 0,
    });

    const cardList = computed(() => {
      const items = props.fields?.items;
      if (items && Array.isArray(items) && items.length > 0) {
        return items.map((item) => ({
          title: item.fields?.title?.value || '',
          desc: item.fields?.description?.value || '',
          image: item.fields?.image?.value?.src || '',
        }));
      }
      return [];
    });

    const methods = {
      handleCardEnter(index) {
        state.activeIndex = index;
      },
      handleCardClick(index) {
        state.activeIndex = index;
      },
    };

    const rootClass = computed(() =>
      utils.overall.baseComClass(props.fields, 'content-card-slider')
    );

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    return {
      state,
      cardList,
      ...methods,
      rootClass,
      rootStyle,
      utils,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.content-card-slider {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    text-align: center;
  }

  &-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 8px;
    text-align: center;
  }

  &-wrapper {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;

    &-item {
      position: relative;
      border-radius: 12px;
      overflow: hidden;
      cursor: pointer;
      height: 240px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      border-radius: 14px;
      height: 280px;

      &-bg {
        position: absolute;
        inset: 0;
        z-index: 0;
        overflow: hidden;
        width: 100%;
        height: 100%;

        &-before {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-position: center center;
          }
        }

        &-after {
          display: none;
        }
      }

      &-content {
        position: absolute;
        inset: 0;
        z-index: 1;
        color: $white;

        &-title {
          position: absolute;
          top: 16px;
          left: 16px;
          font-size: 16px;
          font-weight: 600;
          line-height: 1.3;
        }

        &-desc {
          position: absolute;
          bottom: 16px;
          left: 16px;
          right: 16px;
          line-height: 1.5;
          opacity: 0.9;
          @include line-clamp(2);
          font-size: 14px;
        }
      }

      &-arrow {
        display: none;
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-desc {
      font-size: 18px;
    }

    &-wrapper {
      flex-direction: row;
      gap: 30px;
      height: 360px;
      align-items: stretch;
      margin-top: 40px;

      &-item {
        border-radius: 16px;
        height: 100%;
        flex: 1;
        transition: flex 1s cubic-bezier(0.4, 0, 0.2, 1);

        &.is-active {
          flex: 2.5;
        }

        &.is-active &-bg {
          &-before {
            opacity: 0;
            transition: opacity 1s cubic-bezier(0.4, 0, 0.2, 1);
          }

          &-after {
            opacity: 1;
          }
        }

        &-bg {
          &-after {
            display: block;
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
            opacity: 0;
            transition: opacity 1s cubic-bezier(0.4, 0, 0.2, 1);

            img {
              width: 100%;
              height: 100%;
              object-position: center center;
            }
          }
        }

        &-content {
          padding: 32px;

          &-title {
            top: 24px;
            left: 24px;
            font-size: 20px;
          }

          &-desc {
            bottom: 24px;
            left: 24px;
            right: 24px;
            font-size: 16px;
            @include line-clamp(3);
          }
        }

        &-arrow {
          display: flex;
          position: absolute;
          top: 0;
          right: 0;
          z-index: 1;
          width: 79px;
          height: 69px;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }
}
</style>
